import type { AuctisyncSupabase } from './client.js';
import type { SaleFeed } from './engine-feed.js';
import { startServerClock } from './lots.js';

/**
 * The countdown clock for a live page — ONE implementation for the website
 * and the bidder app (2026-10-08).
 *
 * The device clock cannot decide when a lot closes: the server does, and a
 * phone running 90 seconds fast must not show a countdown that hits zero while
 * the lot is open. So every countdown reads a SERVER-corrected now:
 *
 *   - a sale on the engine → the engine's own clock, from the feed's `ack`
 *     (refined by half a round trip). The engine closes the lots, so its clock
 *     is the one that matters, and it costs no request at all;
 *   - a sale on the Supabase book → `startServerClock`, which samples
 *     `get_server_time()` every minute, as the website and the app do today.
 *
 * Until the feed's first `ack` (a few hundred milliseconds) the engine clock
 * reads the device's own time.
 *
 * The website's own copy of `startServerClock` (`useServerClock`) moves onto
 * this when the pages adopt the feed — the house rule allows one.
 */
export interface LiveClock {
  /** Server-corrected wall clock, epoch ms. Cheap; call it every tick. */
  nowMs(): number;
  /** The current correction (server minus device), ms. */
  offsetMs(): number;
  /** Whole seconds until `closeTime` on this clock — see `secondsUntil`. */
  secondsLeft(closeTime: string | null | undefined): number;
  stop(): void;
}

export type LiveClockSource =
  | { kind: 'engine'; feed: Pick<SaleFeed, 'serverOffsetMs'> }
  | { kind: 'supabase'; supabase: AuctisyncSupabase; resyncMs?: number };

/**
 * Seconds left until `closeTime`, as a countdown shows them. THE rule, for
 * every layout on both surfaces:
 *
 *   - ROUNDED UP. A countdown reads 0 only when the time is actually up; it
 *     never tells a bidder a lot has ended while it is still taking bids
 *     (rounding down did, for the last second — the second a snipe lands in).
 *   - floored at 0: a close time in the past is "ended", never negative.
 *   - no close time (or an unreadable one) → `Infinity`: "no close time" is
 *     not "ended".
 *
 * Before this the app floored and the website rounded, so the two showed
 * different numbers for the same lot at the same instant.
 */
export function secondsUntil(closeTime: string | null | undefined, nowMs: number): number {
  if (closeTime === null || closeTime === undefined) return Number.POSITIVE_INFINITY;
  const at = Date.parse(closeTime);
  if (!Number.isFinite(at)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.ceil((at - nowMs) / 1000));
}

export function createLiveClock(
  source: LiveClockSource,
  now: () => number = () => Date.now(),
): LiveClock {
  if (source.kind === 'engine') {
    const offset = () => source.feed.serverOffsetMs() ?? 0;
    return {
      nowMs: () => now() + offset(),
      offsetMs: offset,
      secondsLeft: (closeTime) => secondsUntil(closeTime, now() + offset()),
      stop: () => {},
    };
  }
  const server = startServerClock(
    source.supabase,
    source.resyncMs !== undefined ? { resyncMs: source.resyncMs } : undefined,
  );
  return {
    nowMs: () => now() + server.offsetMs(),
    offsetMs: () => server.offsetMs(),
    secondsLeft: (closeTime) => secondsUntil(closeTime, now() + server.offsetMs()),
    stop: () => server.stop(),
  };
}
